import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi } from '../services/api'
import {
  clearAdminToken,
  getAdminToken,
  getRememberMePreference,
  setAdminToken,
  setRememberMePreference,
} from '../utils/adminAuth'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    const token = getAdminToken()
    if (!token) {
      setLoading(false)
      return
    }
    try {
      const { data } = await authApi.me()
      setUser(data.data || data)
    } catch {
      clearAdminToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = async (credentials, rememberMe = getRememberMePreference()) => {
    const { data } = await authApi.login(credentials)
    const token = data.token || data.access_token
    setAdminToken(token, rememberMe)
    setRememberMePreference(rememberMe, credentials.username?.trim() || '')
    setUser(data.user?.data || data.user || data.data || data)
    return data
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      /* ignore */
    }
    clearAdminToken()
    setUser(null)
  }

  const hasModuleAccess = useCallback(
    (module, action = 'can_view') => {
      if (!user) return false
      if (user.is_admin) return true
      const permission = user.permissions?.find((item) => item.module === module)
      return permission ? Boolean(permission[action]) : false
    },
    [user],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user || !!getAdminToken(),
        login,
        logout,
        checkAuth,
        hasModuleAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
